import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchJockey } from "../lib/api";
import SearchBar from "../components/SearchBar";
import { Horse, ArrowLeft, User, Trophy } from "@phosphor-icons/react";

const JOCKEY_IMG = "https://images.pexels.com/photos/32563145/pexels-photo-32563145.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function JockeyProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJockey(id)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="mono-label text-zinc-400">Loading jockey data...</p>
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 text-sm mb-4">Jockey not found</p>
          <Link to="/" className="text-sm font-mono underline">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const { jockey, horses } = data;
  const winRate = jockey.total_rides > 0 ? ((jockey.total_wins / jockey.total_rides) * 100).toFixed(1) : 0;

  return (
    <div data-testid="jockey-profile-page" className="min-h-screen bg-white">
      <header className="border-b border-border px-4 md:px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
        <Link to="/" className="flex items-center gap-2">
          <Horse weight="fill" size={28} className="text-[#E12B00]" />
          <span className="text-lg font-heading font-black tracking-tight text-zinc-900">Racing Form Guide</span>
        </Link>
        <SearchBar />
      </header>

      <main className="p-4 md:p-8 max-w-[1400px] mx-auto">
        <Link to="/" data-testid="back-to-dashboard-jockey" className="inline-flex items-center gap-1 text-xs font-mono text-zinc-400 hover:text-zinc-900 transition-colors mb-6">
          <ArrowLeft weight="bold" size={14} />
          Back to Dashboard
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Jockey Image + Info */}
          <div className="border border-border overflow-hidden">
            <div className="h-48 overflow-hidden">
              <img src={JOCKEY_IMG} alt={jockey.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <h1 className="text-2xl font-heading font-black tracking-tight text-zinc-900">{jockey.name}</h1>
              <p className="text-sm text-zinc-500 mt-1">{jockey.nationality} &middot; Age {jockey.age}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="border border-border p-4">
              <p className="mono-label text-zinc-400 mb-2">Career Wins</p>
              <p className="font-mono text-3xl font-bold text-[#008A27]">{jockey.total_wins?.toLocaleString()}</p>
            </div>
            <div className="border border-border p-4">
              <p className="mono-label text-zinc-400 mb-2">Total Rides</p>
              <p className="font-mono text-3xl font-bold text-zinc-900">{jockey.total_rides?.toLocaleString()}</p>
            </div>
            <div className="border border-border p-4">
              <p className="mono-label text-zinc-400 mb-2">Win Rate</p>
              <p className="font-mono text-3xl font-bold text-zinc-900">{winRate}%</p>
            </div>
          </div>
        </div>

        {/* Horses */}
        <div>
          <p className="mono-label text-zinc-500 mb-3">
            <Horse weight="bold" size={12} className="inline mr-1" />
            Current Horses ({horses.length})
          </p>
          <div className="border border-border divide-y divide-border">
            {horses.map((h) => (
              <Link
                key={h.id}
                to={`/horse/${h.id}`}
                data-testid={`jockey-horse-${h.id}`}
                className="flex items-center justify-between p-3 hover:bg-zinc-50 transition-colors group"
              >
                <div>
                  <p className="text-sm font-semibold group-hover:text-[#E12B00] transition-colors">{h.name}</p>
                  <p className="text-xs text-zinc-500">{h.age}yo {h.sex} &middot; {h.trainer}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Trophy weight="fill" size={12} className="text-[#008A27]" />
                  <span className="font-mono text-sm font-bold">{h.total_wins}/{h.total_races}</span>
                </div>
              </Link>
            ))}
            {horses.length === 0 && (
              <p className="text-sm text-zinc-400 p-4 text-center">No horses found for this jockey</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
